import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { subscriptionsAPI } from "../services/api";
import SubscriptionCard from "../components/SubscriptionCard";
import CreateSubscriptionModal from "../components/CreateSubscriptionModal";
import LoadingSpinner from "../components/LoadingSpinner";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: #2d5016;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const CreateButton = styled(motion.button)`
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(45, 80, 22, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(45, 80, 22, 0.6);
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  background: white;
  padding: 0.5rem;
  border-radius: 50px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FilterTab = styled.button`
  background: ${(props) => (props.$active ? "#2d5016" : "transparent")};
  color: ${(props) => (props.$active ? "white" : "#666")};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? "#2d5016" : "rgba(45, 80, 22, 0.1)"};
    color: ${(props) => (props.$active ? "white" : "#2d5016")};
  }
`;

const SubscriptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  color: #2d5016;
`;

const EmptyTitle = styled.h3`
  color: #2d5016;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const StatNumber = styled.div`
  color: #2d5016;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 1rem;
  font-weight: 500;
`;

const Subscriptions = () => {
  const {} = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await subscriptionsAPI.getSubscriptions();
      const subs = response.data.data || [];
      setSubscriptions(subs);

      // Calculate stats
      const total = subs.length;
      const active = subs.filter((sub) => sub.status === "active").length;
      const completed = subs.filter((sub) => sub.status === "completed").length;

      setStats({ total, active, completed });
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionUpdate = () => {
    fetchSubscriptions();
  };

  const filteredSubscriptions = subscriptions.filter((subscription) => {
    if (activeFilter === "all") return true;
    return subscription.status === activeFilter;
  });

  if (loading) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>My Meal Plans</Title>
          <Subtitle>
            View and manage your purchased meal plans. You can purchase a new
            plan anytime.
          </Subtitle>
        </motion.div>
      </Header>

      <Content>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <StatsGrid>
            <StatCard
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>{stats.total}</StatNumber>
              <StatLabel>Total Subscriptions</StatLabel>
            </StatCard>
            <StatCard
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>{stats.active}</StatNumber>
              <StatLabel>Active</StatLabel>
            </StatCard>
            <StatCard
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>{stats.completed}</StatNumber>
              <StatLabel>Completed</StatLabel>
            </StatCard>
          </StatsGrid>

          <ActionBar>
            <FilterTabs>
              <FilterTab
                $active={activeFilter === "all"}
                onClick={() => setActiveFilter("all")}
              >
                All
              </FilterTab>
              <FilterTab
                $active={activeFilter === "active"}
                onClick={() => setActiveFilter("active")}
              >
                Active
              </FilterTab>
              <FilterTab
                $active={activeFilter === "completed"}
                onClick={() => setActiveFilter("completed")}
              >
                Completed
              </FilterTab>
            </FilterTabs>

            <CreateButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
            >
              + New Subscription
            </CreateButton>
          </ActionBar>

          {filteredSubscriptions.length === 0 ? (
            <EmptyState
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <EmptyIcon>🍽️</EmptyIcon>
              <EmptyTitle>
                {activeFilter === "all"
                  ? "No subscriptions yet"
                  : `No ${activeFilter} subscriptions`}
              </EmptyTitle>
              <EmptyText>
                {activeFilter === "all"
                  ? "Start your healthy eating journey by subscribing to a meal plan"
                  : `You don't have any ${activeFilter} subscriptions at the moment`}
              </EmptyText>
              {activeFilter === "all" && (
                <CreateButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCreateModal(true)}
                >
                  Browse Meal Plans
                </CreateButton>
              )}
            </EmptyState>
          ) : (
            <SubscriptionsGrid>
              {filteredSubscriptions.map((subscription, index) => (
                <motion.div
                  key={subscription.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <SubscriptionCard
                    subscription={subscription}
                    onUpdate={handleSubscriptionUpdate}
                  />
                </motion.div>
              ))}
            </SubscriptionsGrid>
          )}
        </motion.div>
      </Content>

      {showCreateModal && (
        <CreateSubscriptionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchSubscriptions();
          }}
        />
      )}
    </Container>
  );
};

export default Subscriptions;
